import { t } from 'i18next';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Form } from 'core/components';
import { FormSectionHeader } from 'core/elements';
import { AlternateVersionHelper } from 'core/helpers';
import { FormProvider, useNotificationContext } from 'core/states';
import { getEmptyErrorMessage } from 'core/yup';
import { CorporateLocationFormField } from 'corporate/elements';
import { CorporateLocationType } from 'corporate/enums';
import { QuoteEntityType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { ICustomerQuote, IUpdateCustomerQuoteLocationForm } from 'quotes/types';

import { CorporateLocationDetails } from './CorporateLocationDetails';
import { EmployeeField, EmployeeType } from './EmployeeField';
import { getDefaultValuesFromQuote } from './utils';

interface IProps {
  isOpen: boolean;
  quote?: ICustomerQuote;
  onClose: (updatedQuote?: ICustomerQuote) => void;
  entityType?: QuoteEntityType;
}

export const UpdateQuotedByLocation = ({
  isOpen,
  quote,
  onClose,
  entityType = QuoteEntityType.QUOTE,
}: IProps) => {
  const { clearNotification } = useNotificationContext();
  const { upsertCustomerQuote } = useQuoteActions({ entityType });

  const { t } = useTranslation();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ quote });

  const defaultValues = useMemo(
    () => getDefaultValuesFromQuote(quote, isAlternateVersionEnabled),
    [quote, isAlternateVersionEnabled]
  );

  const onSubmit =
    (reset: UseFormReset<IUpdateCustomerQuoteLocationForm>) =>
    async (data: IUpdateCustomerQuoteLocationForm) => {
      const { success, quote: updatedQuote } = await upsertCustomerQuote({
        quote: {
          id: quote.id,
          locationId: data?.location?.id,
          ...(isAlternateVersionEnabled && {
            isrEmployeeId: data?.isr?.employeeId || null,
            isrEmployeeName: data?.isr?.employeeName || null,
            isrEmail: data?.isr?.email || null,
            osrEmployeeId: data?.osr?.employeeId || null,
            osrEmployeeName: data?.osr?.employeeName || null,
            osrEmail: data?.osr?.email || null,
          }),
        },
      });

      if (!success) {
        return;
      }

      handleOnClose(reset, updatedQuote);
    };

  const handleOnClose = (
    reset: UseFormReset<IUpdateCustomerQuoteLocationForm>,
    updatedQuote?: ICustomerQuote
  ) => {
    clearNotification();

    onClose(updatedQuote ? { ...quote, ...updatedQuote } : undefined);

    reset(
      updatedQuote
        ? getDefaultValuesFromQuote(updatedQuote, isAlternateVersionEnabled)
        : (defaultValues as IUpdateCustomerQuoteLocationForm)
    );
  };

  return (
    <FormProvider
      schema={customerQuoteUpdateLocationSchema(entityType, isAlternateVersionEnabled)}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <Form
        title={t(`titleUpdate${entityType}Location`)}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        submitBtnLabel={t(`buttonLabelUpdate${entityType}Location`)}
      >
        <FormSectionHeader title={t('titleLocation')} />

        <CorporateLocationFormField
          name="location"
          label={t(`label${entityType}Location`)}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          type={CorporateLocationType.SALES}
        />

        <CorporateLocationDetails isAlternateVersionEnabled={isAlternateVersionEnabled} />

        {isAlternateVersionEnabled && (
          <>
            <FormSectionHeader title={t('titleInsideSalesRepresentative')} />
            <EmployeeField name={EmployeeType.ISR} label={t('labelIsrEmployee')} />

            <FormSectionHeader title={t('titleOutsideSalesRepresentative')} />
            <EmployeeField name={EmployeeType.OSR} label={t('labelOsrEmployee')} />
          </>
        )}
      </Form>
    </FormProvider>
  );
};

const customerQuoteUpdateLocationSchema = (
  entityType: QuoteEntityType,
  isAlternateVersionEnabled: boolean
) =>
  yup.object().shape({
    location: yup
      .object()
      .label(t(`label${entityType}Location`))
      .nullable()
      .required(getEmptyErrorMessage(t(`label${entityType}Location`))),
    ...(isAlternateVersionEnabled && {
      isr: employeeFieldSchema(QuoteEntityType.ORDER === entityType, t('labelIsrEmployee')),
      osr: employeeFieldSchema(QuoteEntityType.ORDER === entityType, t('labelOsrEmployee')),
    }),
  });

const employeeFieldSchema = (required = false, label = '') => {
  return yup
    .object()
    .shape({
      default: yup.boolean().nullable(),
      employeeId: yup
        .string()
        .transform((value) => (value === '' ? null : value))
        .label(t('labelEmployeeId'))
        .nullable()
        .when('default', {
          is: (defaultValue: boolean) => required && !defaultValue,
          then: (schema) => schema.required(getEmptyErrorMessage(t('labelEmployeeId'))),
        }),
      employeeName: yup
        .string()
        .transform((value) => (value === '' ? null : value))
        .label(t('labelName'))
        .nullable()
        .when([], {
          is: () => required,
          then: (schema) => schema.required(getEmptyErrorMessage(t('labelName'))),
        }),
      email: yup
        .string()
        .transform((value) => (value === '' ? null : value))
        .email()
        .label(t('labelEmail'))
        .nullable()
        .when([], {
          is: () => required,
          then: (schema) => schema.required(getEmptyErrorMessage(t('labelEmail'))),
        }),
    })
    .nullable()
    .when([], {
      is: () => required,
      then: (schema) => schema.required(getEmptyErrorMessage(label)),
    });
};
